import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import { usePageHeader } from '../components/Layout';
import { Ic } from '../design/icons';
import { Avatar, StatusPill, PriBar, SectionCard, Donut } from '../design/primitives';
import { statusMeta } from '../design/tokens';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProjectDetail() {
  const { projectId } = useParams();
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [maxFileSize, setMaxFileSize] = useState(50);
  const [isUpdating, setIsUpdating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const { selectedTaskId, isOpen: showDrawer, drawerRef, openDrawer: handleTaskClick, closeDrawer: handleCloseDrawer } = useTaskDrawer();

  // Backend require_workspace_admin gates project create/update/delete in workspace context.
  const canManage = activeWorkspaceId
    ? isWorkspaceAdmin
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_manage_projects')
      );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([fetchProject(), fetchTasks(), fetchUsers()])
      .catch(error => console.error('Proje verileri yüklenirken hata:', error));
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
      setMaxFileSize(response.data.max_file_size_mb || 50);
      const teamsRes = await axios.get(`${API}/teams`);
      const projectTeams = teamsRes.data.filter(t => response.data.teamIds.includes(t.id));
      setTeams(projectTeams);
    } catch (error) {
      toast.error('Proje yüklenemedi');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { params: { project_id: projectId } });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch {}
  };

  const handleUpdateFileSize = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    try {
      const size = parseInt(maxFileSize);
      if (size < 1 || size > 200) {
        toast.error('Dosya boyutu 1 ile 200 MB arası olmalıdır');
        return;
      }
      await axios.patch(`${API}/projects/${projectId}`, { max_file_size_mb: size });
      toast.success('Dosya boyutu limiti güncellendi');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setIsUpdating(false);
    }
  };

  const getUser = (userId) => users.find(u => u.id === userId) || null;

  const normalizeStatus = (s) => {
    const str = String(s || '').toLowerCase();
    if (['pending', 'beklemede', 'todo'].includes(str)) return 'beklemede';
    if (['in_progress', 'işlemde', 'islemde'].includes(str)) return 'işlemde';
    if (['review', 'incelemede'].includes(str)) return 'incelemede';
    if (['completed', 'tamamlandı', 'tamamlandi', 'done'].includes(str)) return 'tamamlandi';
    return 'beklemede';
  };

  const taskStats = useMemo(() => {
    const stats = { total: tasks.length, beklemede: 0, 'işlemde': 0, incelemede: 0, tamamlandi: 0, overdue: 0 };
    const now = new Date();
    tasks.forEach(t => {
      const key = normalizeStatus(t.status);
      stats[key] = (stats[key] || 0) + 1;
      if (key !== 'tamamlandi' && t.dueDate && new Date(t.dueDate) < now) {
        stats.overdue += 1;
      }
    });
    return stats;
  }, [tasks]);

  const completionRate = taskStats.total > 0
    ? Math.round((taskStats.tamamlandi / taskStats.total) * 100)
    : 0;

  const filteredTasks = useMemo(() => {
    if (statusFilter === 'all') return tasks;
    return tasks.filter(t => normalizeStatus(t.status) === statusFilter);
  }, [tasks, statusFilter]);

  const actions = (
    <Link to="/projects" className="ox-btn ox-btn-ghost">
      {Ic.chevL} Projelere Dön
    </Link>
  );

  usePageHeader(
    [{ label: 'Projeler', href: '/projects' }, { label: project?.name || '...' }],
    actions,
    [project?.id]
  );

  if (!project) {
    return (
      <div className="ox-surface rounded-md p-12 text-center ox-text-muted">Yükleniyor...</div>
    );
  }

  const donutSegments = [
    { value: taskStats.beklemede, color: '#8a909a' },
    { value: taskStats['işlemde'], color: '#1E5FA8' },
    { value: taskStats.incelemede, color: '#6c4ca9' },
    { value: taskStats.tamamlandi, color: '#1a7f4f' },
  ].filter(s => s.value > 0);

  return (
    <div className="ox-page space-y-4">
      {/* Project Header */}
      <div className="ox-surface rounded-md p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span style={{ color: 'var(--accent)' }}>{Ic.projects}</span>
              <h1 className="ox-h2 truncate">{project.name}</h1>
            </div>
            {project.description && (
              <div className="ox-page-sub">{project.description}</div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">Tamamlanma</div>
              <div className="ox-tnum text-[22px] font-semibold" style={{ color: 'var(--good)' }}>{completionRate}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="ox-grid-stats-5">
        {[
          { key: 'all', label: 'Toplam', value: taskStats.total, color: 'var(--text)' },
          { key: 'beklemede', label: 'Beklemede', value: taskStats.beklemede, color: 'var(--text-muted)' },
          { key: 'işlemde', label: 'İşlemde', value: taskStats['işlemde'], color: 'var(--accent)' },
          { key: 'incelemede', label: 'İncelemede', value: taskStats.incelemede, color: 'var(--review)' },
          { key: 'tamamlandi', label: 'Tamamlandı', value: taskStats.tamamlandi, color: 'var(--good)' },
        ].map((stat) => (
          <button
            key={stat.key}
            onClick={() => setStatusFilter(stat.key)}
            className="ox-surface rounded-md p-3 text-left"
            style={{
              borderColor: statusFilter === stat.key ? 'var(--accent)' : 'var(--border)',
              cursor: 'pointer',
            }}
          >
            <div className="text-[10.5px] ox-text-muted uppercase tracking-wider">{stat.label}</div>
            <div className="ox-tnum text-[22px] font-semibold mt-1" style={{ color: stat.color }}>{stat.value}</div>
            {stat.key === 'all' && taskStats.overdue > 0 && (
              <div className="text-[10.5px] mt-1" style={{ color: 'var(--danger)' }}>
                {taskStats.overdue} gecikmiş
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Main Grid */}
      <div className="ox-grid-main-1-2">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Completion Donut */}
          <SectionCard title="Görev Dağılımı">
            {donutSegments.length > 0 ? (
              <div className="flex items-center gap-4">
                <Donut segments={donutSegments} size={110} thickness={14} centerLabel="biten" centerValue={`${completionRate}%`} />
                <div className="space-y-1.5 text-[12px]">
                  {taskStats.beklemede > 0 && (
                    <div className="flex items-center gap-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#8a909a' }} />
                      <span className="ox-text-muted">Beklemede</span>
                      <span className="ox-tnum ml-auto font-medium">{taskStats.beklemede}</span>
                    </div>
                  )}
                  {taskStats['işlemde'] > 0 && (
                    <div className="flex items-center gap-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#1E5FA8' }} />
                      <span className="ox-text-muted">İşlemde</span>
                      <span className="ox-tnum ml-auto font-medium">{taskStats['işlemde']}</span>
                    </div>
                  )}
                  {taskStats.incelemede > 0 && (
                    <div className="flex items-center gap-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#6c4ca9' }} />
                      <span className="ox-text-muted">İncelemede</span>
                      <span className="ox-tnum ml-auto font-medium">{taskStats.incelemede}</span>
                    </div>
                  )}
                  {taskStats.tamamlandi > 0 && (
                    <div className="flex items-center gap-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#1a7f4f' }} />
                      <span className="ox-text-muted">Tamamlandı</span>
                      <span className="ox-tnum ml-auto font-medium">{taskStats.tamamlandi}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 ox-text-muted text-[12.5px]">Henüz görev yok</div>
            )}
          </SectionCard>

          {/* Teams */}
          <SectionCard title="Takımlar" subtitle={`${teams.length} takım`}>
            {teams.length === 0 ? (
              <div className="text-center py-4 ox-text-muted text-[12.5px]">Atanmış takım yok</div>
            ) : (
              <div className="space-y-2">
                {teams.map((team) => (
                  <div key={team.id} className="flex items-center gap-2 py-1.5 px-2 rounded"
                    style={{ background: 'var(--surface-muted)' }}>
                    <span style={{ color: 'var(--text-dim)' }}>{Ic.teams}</span>
                    <span className="text-[13px] font-medium flex-1 truncate">{team.name}</span>
                    <span className="ox-tnum text-[11.5px] ox-text-muted">{team.memberIds?.length || 0} üye</span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Managers */}
          {project.managerIds && project.managerIds.length > 0 && (
            <SectionCard title="Proje Yöneticileri" subtitle={`${project.managerIds.length} kişi`}>
              <div className="space-y-2">
                {project.managerIds.map(id => {
                  const manager = getUser(id);
                  if (!manager) return null;
                  return (
                    <div key={id} className="flex items-center gap-2 py-1.5 px-2 rounded"
                      style={{ background: 'var(--surface-muted)' }}>
                      <Avatar u={manager} imgSrc={manager.picture} size={24} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium truncate">{manager.name}</div>
                        {manager.email && <div className="text-[11px] ox-text-muted truncate">{manager.email}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {/* Settings */}
          {canManage && (
            <SectionCard title="Proje Ayarları">
              <form onSubmit={handleUpdateFileSize} className="space-y-3">
                <div className="ox-field">
                  <label>Maks. Dosya Boyutu (MB)</label>
                  <div className="hint">1-200 MB arasında olmalı</div>
                  <input
                    type="number"
                    min="1"
                    max="200"
                    className="ox-input"
                    value={maxFileSize}
                    onChange={(e) => setMaxFileSize(e.target.value)}
                  />
                </div>
                <button type="submit" disabled={isUpdating} className="ox-btn ox-btn-primary w-full">
                  {isUpdating ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
                <div className="text-[11px] ox-text-muted">
                  Mevcut limit: <strong className="ox-tnum">{project.max_file_size_mb || 50} MB</strong>
                </div>
              </form>
            </SectionCard>
          )}
        </div>

        {/* Right Column - Tasks */}
        <SectionCard
          title="Proje Görevleri"
          subtitle={statusFilter === 'all' ? `${tasks.length} görev` : `${filteredTasks.length} görev (${statusMeta(statusFilter).label})`}
          right={
            statusFilter !== 'all' && (
              <button className="ox-btn ox-btn-ghost text-[11.5px]" onClick={() => setStatusFilter('all')}>
                Tümünü göster
              </button>
            )
          }
          pad={false}
        >
          {filteredTasks.length === 0 ? (
            <div className="p-12 text-center">
              <div className="ox-text-dim mb-2 inline-flex">{Ic.tasks}</div>
              <div className="text-[13px] ox-text-muted">
                {statusFilter === 'all' ? 'Henüz görev yok' : 'Bu durumda görev yok'}
              </div>
            </div>
          ) : (
            <div className="divide-y" style={{ '--tw-divide-opacity': 1 }}>
              {filteredTasks.map((task) => {
                const assignee = getUser(task.assignedTo);
                const due = task.dueDate ? new Date(task.dueDate) : null;
                const isOverdue = due && due < new Date() && normalizeStatus(task.status) !== 'tamamlandi';
                return (
                  <div
                    key={task.id}
                    onClick={() => handleTaskClick(task.id)}
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer"
                    style={{ borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-muted)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <PriBar p={task.priority} />
                    <span className="ox-mono text-[10.5px] ox-text-dim" style={{ minWidth: 48 }}>
                      #{String(task.id).slice(-6)}
                    </span>
                    <StatusPill s={task.status} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{task.title}</div>
                    </div>
                    {assignee && <Avatar u={assignee} imgSrc={assignee.picture} size={22} />}
                    {due && (
                      <span className={`ox-mono text-[11px] ${isOverdue ? '' : 'ox-text-muted'}`}
                        style={{ color: isOverdue ? 'var(--danger)' : undefined, minWidth: 70, textAlign: 'right' }}>
                        {due.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>

      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseDrawer}
        onDataChanged={fetchTasks}
      />
    </div>
  );
}
